import React, { useState, useEffect, useMemo, useCallback } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { COLORS } from "./ReCharts"; // Assume your COLORS array is defined similarly here

// LegendKey interface
export interface LegendKey {
  name?: string;
  dataKey: string;
  legendColor?: string;
  [key: string]: any;
}

interface PieChartData {
  [key: string]: string | number;
  name: string;
  value: number;
}

interface PieChartProps {
  data: PieChartData[];
  title?: string;
  dataKey: string;
  nameKey: string;
}

// Helper function to get the correct color for the legend and pie slices
const getLegendColor = (
  name: string,
  index: number,
  selectedLegends: string[]
): string => {
  const isSelected =
    selectedLegends.includes(name) || selectedLegends.length === 0;
  return isSelected ? COLORS[index % COLORS.length] : "#E0E0E0";
};

// Legend component
const LegendComponent = ({
  name,
  index,
  selectedLegends,
  handleLegendClick,
}: {
  name: string;
  index: number;
  selectedLegends: string[];
  handleLegendClick: (name: string) => void;
}) => (
  <div
    key={name}
    className="bar-legend"
    onClick={() => handleLegendClick(name)}
    style={{ cursor: "pointer" }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        color: getLegendColor(name, index, selectedLegends),
      }}
    >
      <div
        className="dot"
        style={{
          marginRight: "6px",
          backgroundColor: getLegendColor(name, index, selectedLegends),
          borderRadius: "50%",
          width: "12px",
          height: "12px",
        }}
      ></div>
      {name}
    </div>
  </div>
);

const RePieChart: React.FC<PieChartProps> = ({
  data,
  title,
  dataKey,
  nameKey,
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [selectedLegends, setSelectedLegends] = useState<string[]>([]);

  const handleLegendClick = useCallback((name: string) => {
    setSelectedLegends((prevSelectedLegends) =>
      prevSelectedLegends.includes(name)
        ? prevSelectedLegends.filter((item) => item !== name)
        : [...prevSelectedLegends, name]
    );
  }, []);

  useEffect(() => {
    if (selectedLegends.length === data.length && selectedLegends.length !== 0) {
      setSelectedLegends([]); // Reset legends when all are selected
    }
  }, [selectedLegends, data]);

  // Memoized to handle filtering of pie data based on selected legends
  const filteredData = useMemo(() => {
    if (selectedLegends.length === 0) return data;
    return data.filter((item) =>
      selectedLegends.includes(item[nameKey] as string)
    );
  }, [data, selectedLegends, nameKey]);

  return (
    <div className="chart-container">
      {title && <h3>{title}</h3>}
      <ResponsiveContainer width="100%" height="80%" >
        <PieChart>
          <Pie
            data={filteredData}
            dataKey={dataKey}
            nameKey={nameKey}
            fill="#8884d8"
            onMouseEnter={(_, index) => setActiveIndex(index)}
            onMouseLeave={() => setActiveIndex(null)}
          >
            {filteredData.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getLegendColor(entry[nameKey] as string, index, selectedLegends)}
                stroke={activeIndex === index ? "#000000" : undefined} // Highlight the active slice
              />
            ))}
          </Pie>
          <Tooltip
          formatter={(value: number, name) => [value.toFixed(2), name]}
          />
        </PieChart>
      </ResponsiveContainer>

      {/* Render the legends dynamically from data */}
      <div className="flex flex-wrap justify-center gap-x-2">
        {data.map((entry, index) => (
          <LegendComponent
            key={index}
            name={entry[nameKey] as string}
            index={index}
            selectedLegends={selectedLegends}
            handleLegendClick={handleLegendClick}
          />
        ))}
      </div>
    </div>
  );
};

export default RePieChart;
